import {
  RepresentationData,
  ConditionalFormattingState,
} from '@ardoq/data-model';
import { colors } from '@ardoq/design-tokens';
import type { ComponentGraphGroup, GroupHeaderLayout } from '@ardoq/graph';
import { IconName } from '@ardoq/icons';
import {
  HtmlCanvasVisual,
  type IRenderContext,
  type INode,
} from '@ardoq/yfiles';
import { drawSpriteIcon } from 'tabview/blocks/visual/node/drawSpriteIcon';
import {
  MODERNIZED_BLOCK_DIAGRAM_CORNER_RADIUS as CORNER_RADIUS,
  MODERNIZED_BLOCK_DIAGRAM_CONDITIONAL_FORMATTING_INSET as CONDITIONAL_FORMATTING_INSET,
  MODERNIZED_BLOCK_DIAGRAM_CONDITIONAL_FORMATTING_STROKE_WIDTH as CONDITIONAL_FORMATTING_STROKE_WIDTH,
} from 'yfilesExtensions/styles/modernized/consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_EXPANDER_SIZE as EXPANDER_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING as PADDING,
} from './consts';
import { collapsedComponentGroupShapeY } from 'yfilesExtensions/styles/modernized/leafNode/collapsedComponentGroupShapeY';
import { drawCollapsedComponentGroup } from 'yfilesExtensions/styles/modernized/leafNode/drawCollapsedComponentGroup';
import { readableButtonColor } from './utils';

/** center of the expander */
const EXPANDER_X = PADDING + EXPANDER_SIZE / 2;
/** center of the expander */
const EXPANDER_Y = PADDING + EXPANDER_SIZE / 2;

type PaintColoredGroupHeaderArgs = {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
  nodeWidth: number;
  stripeHeight: number;
  componentColor: string;
  roundedBottom: boolean;
};

const paintColoredGroupHeader = ({
  context,
  x,
  y,
  nodeWidth,
  stripeHeight,
  componentColor,
  roundedBottom,
}: PaintColoredGroupHeaderArgs): void => {
  const right = x + nodeWidth;
  const stripeBottom = y + stripeHeight;
  context.beginPath();
  // Top-left corner (always rounded)
  context.moveTo(x, y + CORNER_RADIUS);
  context.arcTo(x, y, x + CORNER_RADIUS, y, CORNER_RADIUS);
  // Top-right corner (always rounded)
  context.lineTo(right - CORNER_RADIUS, y);
  context.arcTo(right, y, right, y + CORNER_RADIUS, CORNER_RADIUS);

  if (roundedBottom) {
    // Bottom-right corner (rounded)
    context.lineTo(right, stripeBottom - CORNER_RADIUS);
    context.arcTo(
      right,
      stripeBottom,
      right - CORNER_RADIUS,
      stripeBottom,
      CORNER_RADIUS
    );
    // Bottom-left corner (rounded)
    context.lineTo(x + CORNER_RADIUS, stripeBottom);
    context.arcTo(
      x,
      stripeBottom,
      x,
      stripeBottom - CORNER_RADIUS,
      CORNER_RADIUS
    );
  } else {
    // Bottom edge (straight)
    context.lineTo(right, stripeBottom);
    context.lineTo(x, stripeBottom);
  }

  context.closePath();
  context.fillStyle = componentColor;
  context.fill();
};

const paintGroup = (
  context: CanvasRenderingContext2D,
  node: INode,
  group: ComponentGraphGroup,
  representationData: RepresentationData,
  groupHeader: GroupHeaderLayout | null
) => {
  const isComponent = group.isComponent();
  const { isTransparentized, collapsed, isCollapsible } = group;
  const {
    layout: { x, y, width: nodeWidth, height: nodeHeight },
  } = node;
  const componentColor = group.getCssColorAttributes().fill;
  const isConditionallyFormatted = Boolean(
    componentColor &&
    group.getConditionalFormattingState() ===
      ConditionalFormattingState.FORMATTED
  );

  if (collapsed && isComponent) {
    drawCollapsedComponentGroup(
      context,
      { x, y, width: nodeWidth, height: nodeHeight },
      {
        representation: representationData,
        componentColor: componentColor || null,
        shapeY: collapsedComponentGroupShapeY(node),
        expander: isCollapsible
          ? {
              x: x + PADDING,
              y: y + PADDING,
              width: EXPANDER_SIZE,
              height: EXPANDER_SIZE,
            }
          : null,
        isConditionallyFormatted,
        isTransparentized,
      }
    );
    return;
  }

  const { globalAlpha } = context;
  if (isTransparentized) {
    context.globalAlpha = 0.32;
  }

  context.beginPath();
  context.roundRect(x, y, nodeWidth, nodeHeight, CORNER_RADIUS);
  context.fillStyle = colors.grey50;
  context.strokeStyle = colors.grey400;
  context.fill();
  context.stroke();

  // #region conditional formatting border
  if (isConditionallyFormatted && componentColor) {
    const { lineWidth } = context;
    context.beginPath();
    context.roundRect(
      x + CONDITIONAL_FORMATTING_INSET,
      y + CONDITIONAL_FORMATTING_INSET,
      nodeWidth - 2 * CONDITIONAL_FORMATTING_INSET,
      nodeHeight - 2 * CONDITIONAL_FORMATTING_INSET,
      CORNER_RADIUS - CONDITIONAL_FORMATTING_INSET
    );
    context.lineWidth = CONDITIONAL_FORMATTING_STROKE_WIDTH;
    context.strokeStyle = componentColor;
    context.stroke();
    context.lineWidth = lineWidth;
  }
  // #endregion

  if (componentColor) {
    const mainLabelHeight = groupHeader?.mainLabelHeight ?? 0;
    const stripeHeight = Math.max(mainLabelHeight, EXPANDER_SIZE) + 2 * PADDING;
    paintColoredGroupHeader({
      context,
      x,
      y,
      nodeWidth,
      stripeHeight,
      componentColor,
      roundedBottom: collapsed,
    });
  }
  if (isCollapsible) {
    const expanderIconName = collapsed
      ? IconName.UNFOLD_MORE
      : IconName.UNFOLD_LESS;
    drawSpriteIcon(
      expanderIconName,
      EXPANDER_SIZE,
      readableButtonColor(componentColor || colors.grey50),
      x + EXPANDER_X,
      y + EXPANDER_Y,
      context
    );
  }

  context.globalAlpha = globalAlpha;
};

class GroupVisual extends HtmlCanvasVisual {
  constructor(
    private node: INode,
    private graphGroup: ComponentGraphGroup,
    private representationData: RepresentationData,
    private groupHeaderLayout: GroupHeaderLayout | null
  ) {
    super();
  }
  render(_: IRenderContext, ctx: CanvasRenderingContext2D): void {
    paintGroup(
      ctx,
      this.node,
      this.graphGroup,
      this.representationData,
      this.groupHeaderLayout
    );
  }
}
export { GroupVisual };
